// src/lib/opportunity/opportunityClient.ts
//
// Browser-side client API for the Opportunity Engine. Each function maps
// to one Vercel-route + admin-action dispatch (see vercel.json:
// /api/opportunity/* and api/admin/_actionOpportunityDispatch.ts).
//
// Slice 2 wires list / get / create / update-status + the pricing engine
// endpoint that creates a snapshot + revision and bumps the parent
// opportunity's `active_revision_id` in one round-trip.

import { opportunityFetch } from "./opportunityFetch";
import type {
  Opportunity,
  OpportunityPricingInputs,
  OpportunityPricingSnapshot,
  OpportunityRevision,
  OpportunityStatus,
} from "@/types/opportunity";
import type { OpportunityListItem } from "./opportunityListTypes";

export type OpportunityEntitlementResponse = {
  ok: true;
  enabled: boolean;
  organization_id: string;
};

export function getOpportunityEntitlement() {
  return opportunityFetch<OpportunityEntitlementResponse>("/api/opportunity/entitlement", {
    method: "GET",
  });
}

export type OpportunityListResponse = {
  ok: true;
  opportunities: OpportunityListItem[];
};

export function listOpportunities(
  filters: {
    status?: OpportunityStatus | "all";
    archived?: "active" | "archived" | "all";
    assigned_user_id?: string;
    created_by_user_id?: string;
    location_id?: string;
  } = {},
) {
  const q = new URLSearchParams();
  if (filters.status && filters.status !== "all") q.set("status", filters.status);
  if (filters.archived && filters.archived !== "active") q.set("archived", filters.archived);
  if (filters.assigned_user_id) q.set("assigned_user_id", filters.assigned_user_id);
  if (filters.created_by_user_id) q.set("created_by_user_id", filters.created_by_user_id);
  if (filters.location_id) q.set("location_id", filters.location_id);
  const qs = q.toString();
  const path = qs ? `/api/opportunity/list?${qs}` : "/api/opportunity/list";
  return opportunityFetch<OpportunityListResponse>(path, { method: "GET" });
}

export type OpportunityCreatePayload = {
  /**
   * Free-form product type tag. Should match a Deal Type `type_key` or
   * the well-known "other" sentinel. Defaults to "other" if omitted.
   */
  product_type?: string;
  project_name?: string;
  customer_name?: string;
  customer_company?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_address?: string;
  lead_id?: string | null;
  assigned_user_id?: string | null;
  location_id?: string | null;
};

export type OpportunityCreateResponse = {
  ok: true;
  opportunity: Opportunity;
};

export function createOpportunity(payload: OpportunityCreatePayload) {
  return opportunityFetch<OpportunityCreateResponse>("/api/opportunity/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type OpportunityGetResponse = {
  ok: true;
  opportunity: Opportunity;
  active_snapshot: OpportunityPricingSnapshot | null;
  revisions: OpportunityRevision[];
};

export function getOpportunity(id: string) {
  const path = `/api/opportunity/get?id=${encodeURIComponent(id)}`;
  return opportunityFetch<OpportunityGetResponse>(path, { method: "GET" });
}

export type OpportunityUpdateStatusPayload = {
  opportunity_id: string;
  status: OpportunityStatus;
};

export type OpportunityUpdateStatusResponse = {
  ok: true;
  opportunity: Opportunity;
};

export function updateOpportunityStatus(payload: OpportunityUpdateStatusPayload) {
  return opportunityFetch<OpportunityUpdateStatusResponse>("/api/opportunity/update-status", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type OpportunityUpdateCustomerPayload = {
  opportunity_id: string;
  project_name?: string | null;
  customer_name?: string | null;
  customer_company?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  customer_address?: string | null;
};

export function updateOpportunityCustomer(payload: OpportunityUpdateCustomerPayload) {
  return opportunityFetch<{ ok: true; opportunity: Opportunity }>("/api/opportunity/update-customer", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type OpportunityChangeProductTypeResponse = {
  ok: true;
  opportunity: Opportunity;
  dropped_labels: string[];
  kept_labels: string[];
  reran_pricing: boolean;
};

export function changeOpportunityProductType(payload: {
  opportunity_id: string;
  product_type: string;
}) {
  return opportunityFetch<OpportunityChangeProductTypeResponse>(
    "/api/opportunity/change-product-type",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export type OpportunitySaveRevisionPayload = {
  opportunity_id: string;
  inputs: OpportunityPricingInputs;
  notes?: string | null;
};

export type OpportunitySaveRevisionResponse = {
  ok: true;
  opportunity: Opportunity;
  snapshot: OpportunityPricingSnapshot;
  revision: OpportunityRevision;
};

export function saveOpportunityRevision(payload: OpportunitySaveRevisionPayload) {
  return opportunityFetch<OpportunitySaveRevisionResponse>("/api/opportunity/save-revision", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function archiveOpportunity(opportunityId: string) {
  return opportunityFetch<{ ok: true; opportunity: Opportunity }>("/api/opportunity/archive", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ opportunity_id: opportunityId }),
  });
}

export function deleteOpportunity(opportunityId: string) {
  return opportunityFetch<{ ok: true }>("/api/opportunity/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ opportunity_id: opportunityId }),
  });
}

export function reassignOpportunity(payload: {
  opportunity_id: string;
  assigned_user_id: string;
}) {
  return opportunityFetch<{ ok: true; opportunity: Opportunity }>("/api/opportunity/reassign", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateOpportunityContactPrefs(payload: {
  opportunity_id: string;
  use_rep_proposal_contact: boolean;
}) {
  return opportunityFetch<{ ok: true; opportunity: Opportunity }>(
    "/api/opportunity/update-contact-prefs",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function updateOpportunitySchedulingNotes(payload: {
  opportunity_id: string;
  scheduling_notes: string;
}) {
  return opportunityFetch<{ ok: true; opportunity: Opportunity }>(
    "/api/opportunity/update-scheduling-notes",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function updateOpportunityFulfillment(payload: {
  opportunity_id: string;
  fulfillment?: "inventory" | "custom" | null;
  mfg_unit_serial?: string | null;
}) {
  return opportunityFetch<{ ok: true; opportunity: Opportunity }>(
    "/api/opportunity/update-fulfillment",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function createOpportunityFromLead(payload: {
  lead_id: string;
  product_type?: string;
  project_name?: string;
}) {
  return opportunityFetch<OpportunityCreateResponse>("/api/opportunity/create-from-lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getLinkedOpportunityIdForLead(leadId: string) {
  const q = new URLSearchParams({ lead_id: leadId });
  return opportunityFetch<{ ok: true; opportunity_id: string | null }>(
    `/api/opportunity/by-lead?${q.toString()}`,
    { method: "GET" },
  );
}

export type OpportunityCreateLeadFromOppResponse = {
  ok: true;
  lead: import("@/types/lead").Lead;
  opportunity: Opportunity;
};

export function createLeadFromOpportunity(opportunityId: string) {
  return opportunityFetch<OpportunityCreateLeadFromOppResponse>(
    "/api/opportunity/create-lead-from-opp",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ opportunity_id: opportunityId }),
    },
  );
}

export function transferOpportunityPair(payload: {
  opportunity_id: string;
  assigned_user_id?: string;
  location_id?: string;
}) {
  return opportunityFetch<{ ok: true; opportunity: Opportunity; lead_id: string | null }>(
    "/api/opportunity/transfer-pair",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export type OpportunityPushCrmResponse = {
  ok: true;
  already_pushed: boolean;
  external_ghl_opportunity_id: string;
  external_ghl_contact_id: string | null;
  opp_name?: string;
  monetary_value?: number | null;
  proposal_pdf_url?: string | null;
  crm_pushed_at?: string | null;
  opportunity?: Opportunity;
};

export function pushOpportunityToCrm(opportunityId: string, force = false) {
  return opportunityFetch<OpportunityPushCrmResponse>("/api/opportunity/push-crm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ opportunity_id: opportunityId, force }),
  });
}
